import { BudgetDTO } from "../../../dto/budget/BudgetDTO";
import {
    BudgetDetailDTO,
    CompleteBudgetDetailDTO,
    defaultDisposablesBudgetDetailDTO,
    defaultEquipmentBudgetDetailDTO,
    defaultHospitalizationDaysBudgetDetailDTO,
    defaultMedicinesBudgetDetailDTO,
    defaultNutritionalSupportBudgetDetailDTO,
    defaultProfessionalActivityBudgetDetailDTO,
    withDefaultCoveragePercentages,
} from "../../../dto/budget/BudgetDetailDTO";
import { DETAIL_TYPES, withLineValue } from "./billItems";

const toNumber = (value: bigint | number | null | undefined): number => Number(value ?? 0);

const isType = (detail: CompleteBudgetDetailDTO, types: number[]) => types.includes(Number(detail?.type));

/** Campos comunes de un detalle del backend. */
const common = (item: CompleteBudgetDetailDTO) => ({
    budgetdetail: toNumber(item.budgetdetail) || null,
    description: item.description ?? '',
    unitprice: Number(item.unitprice ?? 0),
    valueday: Number(item.valueday ?? 0),
    valuemonth: Number(item.valuemonth ?? 0),
    unitsqty: Number(item.unitsqty ?? 0),
    type: Number(item.type ?? 0),
    itemid: toNumber(item.itemid) || null,
    billableitems: item.billableitems,
    healthentitypercentagecoverage: item.healthentitypercentagecoverage,
    patientpercentagecoverage: item.patientpercentagecoverage,
});

/**
 * Detalles del período (getBudgetDetailByPatient) convertidos a los ítems de la factura, por categoría. Todavía no están
 * en la factura: el importe de cada línea lo calcula el front.
 */
export function mapDetailsToBudgetItems(details: CompleteBudgetDetailDTO[]): Pick<BudgetDTO, 'professionalActivities' | 'medicines' | 'disposables' | 'nutritionalSupportItems' | 'equipments' | 'hospitalizationDays'> {
    return {
        professionalActivities: details.filter(i => isType(i, DETAIL_TYPES.professionalActivities)).map(item => withLineValue(withDefaultCoveragePercentages({
            ...defaultProfessionalActivityBudgetDetailDTO,
            ...common(item),
            nomenclator: toNumber(item.nomenclator),
            hoursbymonth: Number(item.hoursbymonth ?? 0),
            visitsbymonth: Number(item.visitsbymonth ?? 0),
        }))),
        medicines: details.filter(i => isType(i, DETAIL_TYPES.medicines)).map(item => withLineValue(withDefaultCoveragePercentages({
            ...defaultMedicinesBudgetDetailDTO,
            ...common(item),
            item: toNumber(item.item),
            comboMPPDTO: item.comboMPPDTO ?? defaultMedicinesBudgetDetailDTO.comboMPPDTO,
            packageqty: toNumber(item.packageqty),
        }))),
        disposables: details.filter(i => isType(i, DETAIL_TYPES.disposables)).map(item => withLineValue(withDefaultCoveragePercentages({
            ...defaultDisposablesBudgetDetailDTO,
            ...common(item),
            item: toNumber(item.item),
        }))),
        nutritionalSupportItems: details.filter(i => isType(i, DETAIL_TYPES.nutritionalSupportItems)).map(item => withLineValue(withDefaultCoveragePercentages({
            ...defaultNutritionalSupportBudgetDetailDTO,
            ...common(item),
            comboMPPDTO: item.comboMPPDTO ?? defaultNutritionalSupportBudgetDetailDTO.comboMPPDTO,
            packageqty: toNumber(item.packageqty),
        }))),
        equipments: details.filter(i => Number(i.type) === 4).map(item => withLineValue(withDefaultCoveragePercentages({
            ...defaultEquipmentBudgetDetailDTO,
            ...common(item),
            item: toNumber(item.item),
        }))),
        hospitalizationDays: details.filter(i => isType(i, DETAIL_TYPES.hospitalizationDays)).map(item => withLineValue(withDefaultCoveragePercentages({
            ...defaultHospitalizationDaysBudgetDetailDTO,
            ...common(item),
            nomenclator: toNumber(item.nomenclator),
            hoursbymonth: Number(item.hoursbymonth ?? 0),
            visitsbymonth: Number(item.visitsbymonth ?? 0),
        }))),
    };
}

/**
 * Detalles del paciente tal como llegan, separados por categoría (así los cargaba la pantalla al elegir el paciente:
 * sin pasar por el mapeo, conservan honorarios y derechos que manda el backend).
 */
export function splitDetailsByCategory(details: CompleteBudgetDetailDTO[]): Pick<BudgetDTO, 'professionalActivities' | 'medicines' | 'disposables' | 'nutritionalSupportItems' | 'hospitalizationDays'> {
    const pick = <T,>(types: number[]) => (details.filter(i => isType(i, types)).map(withDefaultCoveragePercentages) as unknown as BudgetDetailDTO[])
        .map(withLineValue) as unknown as T[];
    return {
        professionalActivities: pick(DETAIL_TYPES.professionalActivities),
        medicines: pick(DETAIL_TYPES.medicines),
        disposables: pick(DETAIL_TYPES.disposables),
        nutritionalSupportItems: pick(DETAIL_TYPES.nutritionalSupportItems),
        hospitalizationDays: pick(DETAIL_TYPES.hospitalizationDays),
    };
}
